<template>
    <Teleport to="body">
        <div
            v-if="show"
            class="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >
            <div
                class="fixed inset-0 bg-gray-600 bg-opacity-50 transition-opacity"
                @click="emit('close')"
            />

            <div class="flex min-h-screen items-center justify-center p-4">
                <div
                    class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto max-w-2xl"
                    @click.stop
                >
                    <!-- Header -->
                    <div class="flex items-start justify-between p-6">
                        <h3
                            id="modal-title"
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            Question Details
                        </h3>
                        <button
                            type="button"
                            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                            @click="emit('close')"
                        >
                            <X class="h-6 w-6" />
                        </button>
                    </div>

                    <!-- Body -->
                    <div class="px-6 pb-6">
                        <div v-if="loading" class="flex justify-center py-8">
                            <div
                                class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                            ></div>
                        </div>

                        <div v-else-if="question" class="space-y-4">
                            <div
                                v-if="question.image_url"
                                class="mb-4 rounded-lg overflow-hidden"
                            >
                                <img
                                    :src="question.image_url"
                                    alt="Question image"
                                    class="w-full max-h-96 object-contain bg-gray-100 dark:bg-gray-900"
                                />
                            </div>

                            <div>
                                <h4
                                    class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Question
                                </h4>
                                <p
                                    class="text-gray-900 dark:text-white whitespace-pre-wrap"
                                >
                                    {{ question.question }}
                                </p>
                            </div>

                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <h4
                                        class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        Type
                                    </h4>
                                    <span
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="getTypeClass(question.type)"
                                    >
                                        {{ question.type }}
                                    </span>
                                </div>

                                <div>
                                    <h4
                                        class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        Difficulty
                                    </h4>
                                    <span
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="
                                            getDifficultyClass(
                                                question.difficulty,
                                            )
                                        "
                                    >
                                        {{ question.difficulty }}
                                    </span>
                                </div>

                                <div>
                                    <h4
                                        class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        Points
                                    </h4>
                                    <p class="text-gray-900 dark:text-white">
                                        {{ question.points }}
                                    </p>
                                </div>

                                <div>
                                    <h4
                                        class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        Status
                                    </h4>
                                    <span
                                        v-if="question.is_active"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Active
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    >
                                        Inactive
                                    </span>
                                </div>

                                <div>
                                    <h4
                                        class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        Subject
                                    </h4>
                                    <p class="text-gray-900 dark:text-white">
                                        {{ question.subject?.name || "-" }}
                                    </p>
                                </div>

                                <div>
                                    <h4
                                        class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                    >
                                        Teacher
                                    </h4>
                                    <p class="text-gray-900 dark:text-white">
                                        {{
                                            question.teacher?.user?.name || "-"
                                        }}
                                    </p>
                                </div>
                            </div>

                            <div
                                v-if="question.tags && question.tags.length > 0"
                            >
                                <h4
                                    class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Tags
                                </h4>
                                <div class="flex flex-wrap gap-2">
                                    <span
                                        v-for="(tag, index) in question.tags"
                                        :key="index"
                                        class="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded"
                                    >
                                        {{ tag }}
                                    </span>
                                </div>
                            </div>

                            <div
                                v-if="
                                    question.options &&
                                    question.options.length > 0 &&
                                    question.type !== 'essay'
                                "
                            >
                                <h4
                                    class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Options
                                </h4>
                                <div class="space-y-2">
                                    <div
                                        v-for="option in sortedOptions"
                                        :key="option.id"
                                        class="flex items-start p-3 rounded-md"
                                        :class="
                                            option.is_correct
                                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                                : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                                        "
                                    >
                                        <span
                                            class="flex-shrink-0 w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full mr-3"
                                            :class="
                                                option.is_correct
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                            "
                                        >
                                            {{
                                                option.option_letter ||
                                                option.option_key
                                            }}
                                        </span>
                                        <p
                                            class="text-sm text-gray-900 dark:text-white flex-1"
                                        >
                                            {{ option.option_text }}
                                        </p>
                                        <Check
                                            v-if="option.is_correct"
                                            class="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 ml-2"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div v-if="question.explanation">
                                <h4
                                    class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Explanation
                                </h4>
                                <div
                                    class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md"
                                >
                                    <p
                                        class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap"
                                    >
                                        {{ question.explanation }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div
                            v-else
                            class="text-center py-8 text-gray-500 dark:text-gray-400"
                        >
                            Failed to load question details
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useQuestionBankStore } from "@/stores/questionBankStore";
import type { QuestionBank, QuestionOption } from "@/types/questionBank";
import { Check, X } from "@lucide/vue";

interface Props {
    show: boolean;
    questionId: string | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    close: [];
}>();

const questionBankStore = useQuestionBankStore();
const question = ref<QuestionBank | null>(null);
const loading = ref(false);

const sortedOptions = computed(() => {
    if (!question.value?.options) return [];
    return [...question.value.options].sort(
        (a: QuestionOption, b: QuestionOption) => a.order - b.order,
    );
});

watch(
    () => props.show,
    async (newValue) => {
        if (newValue && props.questionId) {
            loading.value = true;
            try {
                question.value = await questionBankStore.fetchQuestion(
                    props.questionId,
                );
            } catch (error) {
                console.error("Failed to fetch question:", error);
            } finally {
                loading.value = false;
            }
        } else if (!newValue) {
            question.value = null;
        }
    },
);

const getTypeClass = (type: string) => {
    const typeClasses: Record<string, string> = {
        multiple_choice:
            "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        multiple_select:
            "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
        true_false:
            "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        essay: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    };
    return (
        typeClasses[type] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};

const getDifficultyClass = (difficulty: string) => {
    const difficultyClasses: Record<string, string> = {
        easy: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        hard: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    };
    return (
        difficultyClasses[difficulty] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};
</script>
